import type {
  ObjVal,
  Prettify,
  UnionToIntersection,
  Widen,
} from '@repo/internal-tools-type-utils/src/utils.ts'
import type { ReactNode } from 'react'
import * as v from 'valibot'

export type SettingItemTypeBoolean = {
  type: 'boolean'
  /**
   * A required field that must match the type defined in `type`.
   */
  default: boolean
}

export type SettingItemTypeStringOption = {
  label: string
  value: string
}

export type SettingItemTypeString = {
  type: 'string'
  /**
   * A required field that must match the type defined in `type`.
   */
  default: string
  /**
   * Data type string can have predefined options.
   */
  options?: Array<SettingItemTypeStringOption>
}

export type CommonSettingItemConfig = {
  /**
   * This can be ReactNode but not values that it will render nothing.
   */
  description: Exclude<ReactNode, undefined | null | boolean>
}

export type SettingItemConfig = CommonSettingItemConfig &
  (SettingItemTypeBoolean | SettingItemTypeString)

export type SettingItemType = SettingItemConfig['type']

/**
 * camelCase string with dot separated.
 * The last chunk is the label.
 * The rest in the front is the nested category.
 *
 * Example:
 * id: 'experiment.agentAssist.answerPolishingUi'
 * - label: 'answerPolishingUi'
 * - category: 'experiment.agentAssist'
 */
export type SettingItemConfigId = string

/**
 * A section that defines a group of settings.
 */
export type SettingSectionConfig = {
  items: Record<SettingItemConfigId, SettingItemConfig>
}

/**
 * Used as the section title.
 * format: camelCase string without dot.
 *
 * When the sectionId matches the first chunk of the itemId,
 * the sectionId will be removed from the itemId
 * when displaying in the setting form.
 *
 * This ensures we can have setting item shortcuts in another section.
 */
export type SettingSectionId = string

/**
 * The root level config for all settings.
 */
export type SettingsConfig = {
  /**
   * This will be used to differentiate between setting stores.
   */
  settingsId: string
  sections: Record<SettingSectionId, SettingSectionConfig>
}

export type SettingItemValue<ItemConfig extends SettingItemConfig> =
  // Tries to infer options as literal union instead of widened default value.
  ItemConfig extends SettingItemTypeString
    ? ItemConfig['options'] extends Array<infer Option>
      ? Option extends SettingItemTypeStringOption
        ? Option['value']
        : Widen<ItemConfig['default']>
      : Widen<ItemConfig['default']>
    : Widen<ItemConfig['default']>

export type SettingSectionValues<SectionConfig extends SettingSectionConfig> = {
  -readonly [I in keyof SectionConfig['items']]?: SettingItemValue<
    SectionConfig['items'][I]
  >
}

/**
 * The configs of all setting items by itemId.
 *
 * {
 *   [itemConfigId]: <SettingItemConfig>
 * }
 */
export type FlattenedSettingItemConfigs<RootConfig extends SettingsConfig> =
  Prettify<
    UnionToIntersection<
      ObjVal<{
        [SectionId in Extract<
          keyof RootConfig['sections'],
          SettingSectionId
        >]: {
          [ItemId in Extract<
            keyof RootConfig['sections'][SectionId]['items'],
            SettingItemConfigId
          >]: RootConfig['sections'][SectionId]['items'][ItemId]
        }
      }>
    >
  >

/**
 * The values of all setting items by itemId.
 *
 * {
 *   // Partial because every value is optional and may not exist in the storage.
 *   [itemConfigId]?: <value type>,
 * }
 */
export type FlattenedSettingValues<RootConfig extends SettingsConfig> = Partial<
  Prettify<
    UnionToIntersection<
      ObjVal<{
        [SectionId in Extract<
          keyof RootConfig['sections'],
          SettingSectionId
        >]: {
          [ItemId in Extract<
            keyof RootConfig['sections'][SectionId]['items'],
            SettingItemConfigId
          >]?: SettingItemValue<
            RootConfig['sections'][SectionId]['items'][ItemId]
          >
        }
      }>
    >
  >
>

const SETTING_ITEM_TYPE_BOOLEAN_SCHEMA = v.optional(
  v.union([
    v.boolean(),
    v.pipe(
      v.string(),
      v.transform((value) => value === 'true'),
    ),
  ]),
)

const SETTING_ITEM_TYPE_STRING_SCHEMA = v.optional(v.string())

const ITEM_VALUE_SCHEMA_BY_TYPE = {
  boolean: SETTING_ITEM_TYPE_BOOLEAN_SCHEMA,
  string: SETTING_ITEM_TYPE_STRING_SCHEMA,
} as const satisfies Record<SettingItemType, unknown>

export const safeParseItemValue = <ItemConfig extends SettingItemConfig>(
  itemConfig: ItemConfig,
  value: unknown,
): SettingItemValue<ItemConfig> => {
  const schema = ITEM_VALUE_SCHEMA_BY_TYPE[itemConfig.type]
  if (!schema) return itemConfig.default as SettingItemValue<ItemConfig>

  const { output } = v.safeParse(schema, value)
  return (output ?? itemConfig.default) as SettingItemValue<ItemConfig>
}
